import { type FunctionComponent, type HTMLAttributes, type SyntheticEvent, useState } from 'react';
import { type ProductBundleItemsFieldsFragment } from '../../../__generated__/graphql-client-types';
import { formatPrice } from '../../../helpers/general-helper/general-helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { useAddToCartAction, type AddProductToCartParams } from '../../../hooks/add-to-cart/add-to-cart.hooks';
import { type ProductBundleItem } from '../../../types/product.types';
import { Link } from '../../common-components/link/link.component';
import { PanelComponent } from '../../common-components/panel-component/panel-component.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import AddIcon from '../../svgs/add.svg';
import { AddToCartButtonFactory } from '../add-to-cart-button/add-to-cart-button.component';

export type ProductBundleItemProps = { showPlusSign?: boolean } & ProductBundleItem;

/**
 * Product Bundle Item Component
 */
const ProductBundleItemComponent: FunctionComponent<ProductBundleItemProps> = ({
	showPlusSign = false,
	modelNumber,
	link,
	quantity,
	priceInfo,
	image,
	isConfigurable,
	finishId,
	familyId
}) => {
	const INFORMATION_CLASSES = 'flex flex-column items-center absolute left-0 right-0 bottom-0 bg-theme-white-o70 pa2';

	const { addToCart, status } = useAddToCartAction();

	const params: AddProductToCartParams = {
		analyticsData: {
			link: 'Add to Cart',
			linkName: 'Add to Cart',
			pageName: 'product:package-item',
			variantId: finishId,
			parentId: familyId
		},
		variantId: finishId,
		quantity: 1
	};

	const [isAddingToCart, setIsAddingToCart] = useState(false);

	const onAddToCartClick = async (e: SyntheticEvent<Element>) => {
		// without the preventDefault, the click goes straight through the button to the parent, which is a Link component
		// and we want the configure button to link to the product's PDP
		if (!isConfigurable) {
			e.preventDefault();
			setIsAddingToCart(true);
			await addToCart(params);
			setIsAddingToCart(false);
		}
	};

	return (
		<li className="flex flex-row items-center w-auto mv2 mh1 hide-child">
			<Link className="relative" url={link.url}>
				<div className="flex flex-column center pa3">
					{image ? (
						<CloudinaryImage
							description={image.description}
							publicID={image.id}
							options={{ width: 120, height: 120, crop: 'lpad' }}
						/>
					) : (
						<CloudinaryImage
							description={modelNumber}
							publicID={'mediabase/noimage.gif'}
							options={{ width: 120, height: 120, crop: 'lpad' }}
						/>
					)}
				</div>
				<div className={`child ${INFORMATION_CLASSES}`}>
					<span>{modelNumber}</span>
					<div className="flex flex-column items-center flex-wrap theme-grey-darker mt1">
						<strong>{formatPrice(priceInfo.current)}</strong>
						{quantity > 1 && <strong className="mt1">Quantity: {quantity}</strong>}
					</div>
					<div className="pa2">
						<AddToCartButtonFactory
							buttonType={'STANDARD'}
							addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.BUNDLE}
							handleOnClick={onAddToCartClick}
							isConfigurable={isConfigurable}
							disabled={false}
							status={status}
							isBusy={isAddingToCart}
						/>
					</div>
				</div>
			</Link>
			{showPlusSign && <AddIcon className="f1 pl1" />}
		</li>
	);
};

export type ProductBundlesProps = ProductBundleItemsFieldsFragment & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Bundles/Package Component
 *
 * @param {ProductBundlesProps} props
 */
export const ProductBundles: FunctionComponent<ProductBundlesProps> = ({ className = '', bundleItems }) => {
	return bundleItems.length > 0 ? (
		<PanelComponent
			headingContent="This package includes the following items"
			headerClassName="pl2 f5 b"
			className={`dn db-ns ${className ?? ''}`.trim()}>
			<ul className="list flex flex-row flex-wrap flex-auto ma0 pa0 w-100">
				{bundleItems.map((bundleItem, index) => (
					<ProductBundleItemComponent key={index} {...bundleItem} showPlusSign={index < bundleItems.length - 1} />
				))}
			</ul>
		</PanelComponent>
	) : null;
};
